import { usePriceInUSD } from 'app/queries/price/hooks';
import { getExplorerLink, useSiloWeb3 } from '@silo/web3';
import { FC, ReactNode, useLayoutEffect, useRef, useState } from 'react';
import { AssetLogo } from '../common/icons/AssetLogo';
import { useThisDecoratedSilo } from './hooks';
import { OracleDescription, OracleLabel } from './OracleLabel';
import { ErrorBoundary } from 'react-error-boundary';
import { useThisSilo } from './useThisSilo';
import { SiloAsset } from './useSilo';
import {
  ADDRESS_ZERO,
  AssetKeyLegacy,
  BigIntWithDecimals,
  ChecksumAddress,
  DEFAULT_PROTOCOL_KEY,
  LINKS,
  UNKNOWN_ORACLE,
  assetKeysLegacy,
  getOracle,
  getPointsLabel,
  getProtocolInstanceChainId,
  makeAddressSync,
} from '@silo/types';
import { useSiloConfiguration } from '@silo/config-legacy';
import {
  asCompactDecimal,
  asCompactUSD,
  asWholePct,
  convertCurrency,
  determineOracleKey,
  isUndefined,
  shortenAddress,
} from '@silo/utils';
import { array, option } from 'fp-ts';
import { pipe } from 'fp-ts/lib/function';
import { getCurveLpAssetAddress, getIsCurveLpAsset } from '../../util/curve-lp';
import { getRewardsTokenSymbol } from '../rewards/config';
import {
  AprDisplay,
  ExternalLink,
  ExternalLinkIcon,
  Perk,
  Oracle,
  PointsLinks,
  StaticDescription,
  Tooltip,
  TooltipContent,
  TooltipOrigin,
  useCopyClipboard,
} from '@silo/ui';
import { Option } from 'effect';
import { getConfigAssetPoints, getConfigDepositMode, getConfigIsDebtAprFixed } from '@silo/config';

interface AssetCardProps {
  asset: SiloAsset & { assetKey: AssetKeyLegacy };
}

interface PointsConfig {
  points: ReactNode;
  link: string;
}

const AssetCard: FC<AssetCardProps> = ({ asset }) => {
  const {
    priceInNative,
    decimals,
    assetAddress,
    symbol,
    name,
    liquidity,
    utilization,
    depositAPY: depositApr,
    depositRewardsApr,
    borrowAPY: borrowApr,
    borrowRewardsApr,
    isNative,
    assetKey,
    merklRewards,
    rawSymbol,
  } = asset;
  const { protocolInstance } = useSiloWeb3();
  const priceInUSD = usePriceInUSD(priceInNative);
  const [isCopied, setCopied] = useCopyClipboard(1000);
  const {
    siloAddress,
    assets: [baseAsset],
  } = useThisDecoratedSilo();

  const { getConfigurationForSilo } = useSiloConfiguration(protocolInstance);
  const siloConfig = getConfigurationForSilo(siloAddress);
  const isCurveLpAsset = getIsCurveLpAsset(siloConfig, assetKey);

  if (isUndefined(protocolInstance)) return null;

  const toDualAmount = (inTokens: BigIntWithDecimals): DualAmount => ({
    // priceInUSD is null when the oracle price is unavailable; show "--" for USD.
    bottom: priceInUSD ? asCompactUSD(convertCurrency(inTokens, priceInUSD)) : '--',
    top: asCompactDecimal(inTokens),
  });

  const displayAddress = getCurveLpAssetAddress(siloConfig, assetKey) ?? assetAddress;
  const chainId = getProtocolInstanceChainId(protocolInstance);
  const explorerLink = getExplorerLink(chainId, displayAddress, 'token');

  const priceProvider = pipe(
    asset.priceProvider,
    option.map(makeAddressSync),
    option.getOrElse(() => ADDRESS_ZERO)
  );

  const protocolKey = protocolInstance ?? DEFAULT_PROTOCOL_KEY;

  const oracleKey = determineOracleKey({
    protocolKey,
    tokenAddress: makeAddressSync(assetAddress),
    isNative,
    priceProvider,
  });

  const { label, riskLevel } = pipe(
    oracleKey,
    Option.fromNullable,
    Option.map(getOracle),
    Option.getOrElse(() => UNKNOWN_ORACLE)
  );

  const topTerms = [
    {
      name: 'Total deposited',
      value: <AssetCardDualAmount value={toDualAmount({ value: asset.supply, decimals })} />,
      dataCy: 'total-deposited',
    },
    ...(isCurveLpAsset
      ? []
      : [
          {
            name: `Available to borrow`,
            value: <AssetCardDualAmount value={toDualAmount(liquidity)} />,
            dataCy: 'left-to-borrow',
          },
        ]),
    ...(isCurveLpAsset ? [] : [{ name: 'Utilization', value: asWholePct(utilization), dataCy: 'utilization' }]),
    ...(oracleKey && !isNative
      ? [
          {
            name: <OracleLabel assetAddress={assetAddress} assetSymbol={rawSymbol} oracleKey={oracleKey} />,
            value: (
              <p className="flex gap-2.5 items-center">
                <Oracle.Indicator riskLevel={riskLevel} />
                {label}
              </p>
            ),
            dataCy: 'oracle',
          },
        ]
      : []),
  ];

  const rewardsSymbol = getRewardsTokenSymbol(protocolInstance);

  const marketAddress = makeAddressSync(siloAddress);
  const depositMode = getConfigDepositMode({ protocolKey, marketAddress, assetKey });
  const isProtected = depositMode === 'protectedOnly' || depositMode === 'defaultFalse';

  const depositAprMetrics = AprDisplay.getDepositAprMetrics({
    depositApr,
    depositRewardsApr,
    rewardsSymbol,
    isProtected,
    merklRewards,
  });

  const borrowAprMetrics = AprDisplay.getBorrowAprMetrics({
    borrowApr,
    borrowRewardsApr,
    rewardsSymbol,
    merklRewards,
  });

  const isDebtAprFixed = getConfigIsDebtAprFixed({ protocolKey, marketAddress, assetKey });

  const points = getConfigAssetPoints({ protocolKey, marketAddress, assetKey });
  const collateralPoints = points.collateral ?? [];
  const protectedPoints = points.protected ?? [];
  const depositPoints = isProtected ? protectedPoints : collateralPoints;
  const debtPoints = points.debt ?? [];

  const hasDepositSuperFest = depositAprMetrics.depositRewards.some((reward) => reward.isSuperfest);
  const { hasDepositRewardsApr } = depositAprMetrics;
  const hasBorrowSuperFest = borrowAprMetrics.borrowRewards.some((reward) => reward.isSuperfest);
  const { hasBorrowRewardsApr } = borrowAprMetrics;

  return (
    <div className="flex-1 p-5 rounded-lg bg-shale-800" data-cy="asset-card">
      <div className="flex flex-col gap-7">
        <div className="flex gap-4 items-center">
          <AssetLogo protocolInstance={protocolInstance} address={assetAddress} size="xl" />
          <div className="overflow-hidden">
            <div className="font-bold text-white" data-cy="asset-symbol">
              {symbol}
            </div>
            <div className="text-shale-200" data-cy="asset-name">
              {name}
            </div>
            <div className="flex gap-1 items-center text-sm text-shale-300">
              <Tooltip placement="bottom" disableCloseOnClick>
                <TooltipOrigin>
                  <button onClick={() => setCopied(displayAddress)} data-cy="asset-address" className="text-sm">
                    {shortenAddress(displayAddress)}
                  </button>
                </TooltipOrigin>
                <TooltipContent>{isCopied ? 'Copied!' : 'Copy'}</TooltipContent>
              </Tooltip>
              <a href={explorerLink} target="_blank" rel="noreferrer">
                <span>
                  <ExternalLinkIcon className="w-4 h-4" />
                </span>
              </a>
            </div>
          </div>
        </div>

        <dl className="flex flex-col gap-7">
          {!isCurveLpAsset && (
            <>
              <div className="flex flex-col gap-5">
                <AprDisplay.AssetCardDeposit
                  depositApr={depositApr}
                  depositRewardsApr={depositRewardsApr}
                  rewardsSymbol={rewardsSymbol}
                  isProtected={isProtected}
                  merklRewards={merklRewards}
                />

                {hasDepositRewardsApr && (
                  <ExternalLink size="small" href={LINKS.SILOPEDIA.INCENTIVES}>
                    Rewards details
                  </ExternalLink>
                )}

                {hasDepositSuperFest && (
                  <ExternalLink href={LINKS.SUPERFEST} size="small" variant="action" className="w-fit !text-sm">
                    SuperFest details
                  </ExternalLink>
                )}
              </div>

              {depositPoints.length > 0 && (
                <div className="flex flex-col gap-5">
                  <Perk.Root>
                    <Perk.Label />

                    {depositPoints.map((pointsItem) => (
                      <Perk.Item>
                        <Perk.PointsIcon pointsKey={pointsItem._tag} className={Perk.iconClassName} />

                        <Perk.Emphasis>{getPointsLabel(pointsItem)}</Perk.Emphasis>

                        <Perk.PointsLabel />
                      </Perk.Item>
                    ))}
                  </Perk.Root>

                  <PointsLinks pointsItems={depositPoints} />
                </div>
              )}

              <div className="h-px bg-shale-600" />

              <div className="flex flex-col gap-5">
                <AprDisplay.AssetCardBorrow
                  borrowApr={borrowApr}
                  borrowRewardsApr={borrowRewardsApr}
                  isBorrowAprFixed={isDebtAprFixed}
                  rewardsSymbol={rewardsSymbol}
                  merklRewards={merklRewards}
                />

                {hasBorrowRewardsApr && (
                  <ExternalLink size="small" href={LINKS.SILOPEDIA.INCENTIVES}>
                    Rewards details
                  </ExternalLink>
                )}

                {hasBorrowSuperFest && (
                  <ExternalLink href={LINKS.SUPERFEST} size="small" variant="action" className="w-fit !text-sm">
                    SuperFest details
                  </ExternalLink>
                )}
              </div>

              {debtPoints.length > 0 && (
                <div className="flex flex-col gap-5">
                  <Perk.Root>
                    <Perk.Label />

                    {debtPoints.map((pointsItem) => (
                      <Perk.Item>
                        <Perk.PointsIcon pointsKey={pointsItem._tag} className={Perk.iconClassName} />

                        <Perk.Emphasis>{getPointsLabel(pointsItem)}</Perk.Emphasis>

                        <Perk.PointsLabel />
                      </Perk.Item>
                    ))}
                  </Perk.Root>

                  <PointsLinks pointsItems={debtPoints} />
                </div>
              )}

              <div className="h-px bg-shale-600" />
            </>
          )}

          <div className="flex flex-col gap-4">
            {topTerms.map(({ name, value, dataCy }, index) => (
              // can use index since content is static
              <div key={index} className="flex justify-between items-baseline">
                <dt className="text-sm font-normal text-shale-200">{name}</dt>
                <dd data-cy={dataCy} className="font-mono font-medium text-white">
                  {value}
                </dd>
              </div>
            ))}

            {oracleKey && !isNative ? (
              <StaticDescription>
                <div className="flex flex-col gap-2.5">
                  <OracleDescription
                    assetAddress={assetAddress}
                    assetSymbol={rawSymbol}
                    oracleKey={oracleKey}
                    hideLearnMoreLink
                  />

                  <Oracle.Description
                    oracleKey={oracleKey}
                    oracleAssets="other"
                    marketAddress={makeAddressSync(siloAddress)}
                    marketName={baseAsset.name}
                    marketSymbol={baseAsset.symbol}
                    rawMarketSymbol={baseAsset.rawSymbol}
                    protocolKey={protocolKey}
                  />
                </div>
              </StaticDescription>
            ) : null}
          </div>
        </dl>
      </div>
    </div>
  );
};

interface DualAmount {
  top: string;
  bottom: string;
}

const AssetCardDualAmount: FC<{ value: DualAmount }> = ({ value: { top, bottom } }) => (
  <div className={`flex flex-col gap-1 items-end`}>
    <span data-cy="top">{top}</span>
    <span data-cy="bottom" className="font-mono text-xs font-normal text-shale-200">
      {bottom}
    </span>
  </div>
);

interface AssetNameProps {
  name: string;
}

/**
 * Keep this for now. Need to figure out if we still need this feature after the redesign
 */
const AssetName: FC<AssetNameProps> = ({ name }) => {
  const assetNameRef = useRef<HTMLSpanElement>(null);
  const [off, setOff] = useState(true);

  useLayoutEffect(() => {
    if (!assetNameRef.current) return;

    const assetNameResizeObserver = new ResizeObserver(() => {
      if (!assetNameRef.current) return;

      const { offsetWidth, scrollWidth } = assetNameRef.current;

      const isTruncated = offsetWidth < scrollWidth;

      setOff(!isTruncated);
    });

    assetNameResizeObserver.observe(assetNameRef.current);

    return (): void => {
      assetNameResizeObserver.disconnect();
    };
  }, []);

  return (
    <Tooltip placement="bottom-start" disableCloseOnClick off={off}>
      <TooltipOrigin className="overflow-hidden">
        <span
          className="overflow-hidden whitespace-nowrap text-shale-200 text-ellipsis"
          data-cy="asset-name"
          ref={assetNameRef}
        >
          {name}
        </span>
      </TooltipOrigin>
      <TooltipContent>{name}</TooltipContent>
    </Tooltip>
  );
};

export const AssetCards: FC = () => {
  const silo = useThisSilo();
  const { siloAddress, assets } = silo;
  const orderedAssets = [assets.base]
    .concat(assets.bridge)
    .map((asset, i) => ({ ...asset, assetKey: assetKeysLegacy[i] }));
  const { left: column1, right: column2 } = pipe(
    orderedAssets,
    array.partitionWithIndex((i) => !!(i % 2))
  );

  return (
    <div className="grid grid-cols-1 gap-5 sm:gap-2.5 sm:grid-cols-2">
      <div className="flex flex-col gap-5 sm:gap-2.5">
        {column1.map((asset) => (
          <SafeAssetCard siloAddress={siloAddress} asset={asset} />
        ))}
      </div>
      <div className="flex flex-col gap-5 sm:gap-2.5">
        {column2.map((asset) => (
          <SafeAssetCard siloAddress={siloAddress} asset={asset} />
        ))}
      </div>
    </div>
  );
};

type SafeAssetCardProps = AssetCardProps & { siloAddress: ChecksumAddress };

const SafeAssetCard: FC<SafeAssetCardProps> = ({ siloAddress, asset }) => {
  return (
    <ErrorBoundary
      fallback={
        <div className="p-5 rounded-lg bg-shale-800">
          <p>
            A system error has occured when displaying asset information. Please contact us for assistance if the error
            persists.
          </p>
          <p>{asset?.assetAddress ? <span className="text-xs opacity-50">{asset?.assetAddress}</span> : null}</p>
        </div>
      }
      key={asset.assetAddress}
    >
      <div>
        <AssetCard asset={asset} />
      </div>
    </ErrorBoundary>
  );
};

export const PulseAssetCards: FC = () => (
  <div className="flex flex-col gap-5 sm:gap-2.5 sm:flex-row animate-pulse">
    <div className="w-full h-80 rounded-lg bg-shale-800"></div>
    <div className="w-full h-80 rounded-lg bg-shale-800"></div>
  </div>
);
